import { MapPin, Phone, Mail, Building2, Users, Presentation, Landmark, Bus, Car, Globe, ArrowRight, Clock, Wifi, Coffee, Shield } from "lucide-react";
import "@/App.css";

function SocialPost() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div
        data-testid="social-post"
        className="w-[1080px] h-[1080px] bg-white relative overflow-hidden flex flex-col"
        style={{ maxWidth: "100%", aspectRatio: "1/1" }}
      >
        {/* Top bar */}
        <div className="bg-[#1a1a1a] px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-white text-xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Crown Workspace
            </span>
            <span className="text-[#B89B72] text-xs font-medium uppercase tracking-widest" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              Luxembourg
            </span>
          </div>
          <span className="text-[#B89B72] text-xs" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            crownworkspace.lu
          </span>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-10 py-8">
          {/* Title + Image row */}
          <div className="flex gap-6 items-start">
            <div className="flex-1">
              <p className="text-[#B89B72] text-sm uppercase tracking-[0.2em] mb-3" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                Espace de travail
              </p>
              <h1 className="text-5xl font-semibold tracking-tight leading-[1.15]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Votre espace<br />de travail<br />
                <span className="italic text-[#B89B72]">à Strassen</span>
              </h1>
              <p className="mt-5 text-[15px] text-[#333333] leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Un cadre agréable et professionnel pour développer votre activité sereinement.
                <span className="font-bold"> Bureaux privés, coworking, salles de réunion et siège social de société</span> au 3ème étage d'un bâtiment bien situé.
              </p>
            </div>
            {/* Image */}
            <div className="w-[280px] h-[220px] flex-shrink-0 relative overflow-hidden border border-black/10">
              <img
                src="https://images.pexels.com/photos/3778613/pexels-photo-3778613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Open space"
                className="w-full h-full object-cover"
              />
              <span className="absolute bottom-1 right-2 text-[8px] text-white/60 bg-black/40 px-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                Non contractuelle
              </span>
            </div>
          </div>

          {/* Services */}
          <div className="grid grid-cols-4 gap-3 mt-7">
            {[
              { icon: Building2, label: "Bureaux Privés" },
              { icon: Users, label: "Espace Coworking" },
              { icon: Presentation, label: "Salle de Réunion" },
              { icon: Landmark, label: "Siège Social" },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="flex items-center gap-3 p-4 border border-black/10 bg-[#FAFAFA]">
                  <Icon size={22} strokeWidth={1.3} className="text-[#B89B72] flex-shrink-0" />
                  <span className="text-sm font-semibold text-[#333333]">{s.label}</span>
                </div>
              );
            })}
          </div>

          {/* Accès facile */}
          <div className="bg-[#1a1a1a] p-5 mt-6 flex items-center justify-between">
            <p className="text-[#B89B72] text-xs uppercase tracking-[0.15em] font-medium" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              Accès facile
            </p>
            <div className="flex items-center gap-8">
              {[
                { icon: Bus, label: "Transports en commun" },
                { icon: Car, label: "Autoroute à 2 min" },
                { icon: MapPin, label: "Proche centre-ville" },
              ].map((a) => {
                const Icon = a.icon;
                return (
                  <div key={a.label} className="flex items-center gap-2">
                    <Icon size={16} strokeWidth={1.5} className="text-[#B89B72]" />
                    <span className="text-sm text-white/90 font-medium">{a.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Extras */}
          <div className="grid grid-cols-4 gap-3 mt-5">
            {[
              { icon: Clock, label: "Accès 24/7" },
              { icon: Wifi, label: "Fibre Haut Débit" },
              { icon: Coffee, label: "Café & Cuisine" },
              { icon: Shield, label: "Sécurité 24h" },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.label} className="flex items-center gap-2 p-4 border border-black/5 bg-[#FAFAFA]">
                  <Icon size={18} strokeWidth={1.3} className="text-[#B89B72] flex-shrink-0" />
                  <span className="text-sm text-[#666666] font-medium">{f.label}</span>
                </div>
              );
            })}
          </div>

          {/* Why choose us */}
          <div className="mt-6 p-6 border-2 border-[#B89B72]/40 bg-[#FAFAF5] flex items-start gap-4">
            <Landmark size={24} strokeWidth={1.3} className="text-[#B89B72] flex-shrink-0 mt-1" />
            <div>
              <p className="text-xl font-semibold text-[#333333] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                Cabinet comptable partenaire sur place
              </p>
              <p className="mt-2 text-sm text-[#666666] leading-relaxed">
                Un cabinet comptable est présent dans le bâtiment, facilitant vos démarches administratives et financières.
              </p>
            </div>
          </div>

          {/* CTA + Contact */}
          <div className="flex items-center justify-between mt-auto pt-5 border-t border-black/10">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Phone size={15} strokeWidth={1.5} className="text-[#B89B72]" />
                <span className="text-base font-semibold text-[#333333]">+352 621 572 444</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={15} strokeWidth={1.5} className="text-[#B89B72]" />
                <span className="text-base text-[#333333]">info@eh-management.lu</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="bg-[#1a1a1a] px-10 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={13} className="text-[#B89B72]" />
            <span className="text-white/80 text-sm font-medium">321, Route d'Arlon — L-8011 Strassen — 3ème étage</span>
          </div>
          <span className="text-[#B89B72] text-sm font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            24/7
          </span>
        </div>
      </div>
    </div>
  );
}

export default SocialPost;
